import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Modal } from './Modal';
import { SlateLogo } from './SlateLogo';
import {
  loadAcknowledgements as defaultLoad,
  type AckData,
  type AckItem,
} from '../data/acknowledgements';

const WELOIN_URL = 'https://weloin.com';
const PRODUCT_URL = 'https://slateeditor.com';
const PRODUCT_LABEL = 'slateeditor.com';
const TAGLINE =
  'A fast, native, cross-platform editor for Markdown, code, and the rich content alongside them.';

export interface AboutDialogProps {
  open: boolean;
  onClose: () => void;
  /** App version string (e.g. "0.1.0"). The version line is hidden when absent. */
  version?: string;
  /** Open an external URL in the OS browser (via the platform bridge). */
  onOpenExternal?: (url: string) => void;
  /** Open with the Acknowledgements list already expanded. */
  showAcknowledgements?: boolean;
  /** Lazy loader for the acknowledgements payload. Injectable for tests. */
  loadAcknowledgements?: () => Promise<AckData>;
}

/**
 * In-app "About Slate" dialog: product identity, links to Weloin and the
 * product site, copyright, and an expandable Acknowledgements list of the
 * bundled third-party open-source components with their verbatim license texts.
 * The (large) license payload is lazy-loaded only when the list is opened.
 */
export function AboutDialog({
  open,
  onClose,
  version,
  onOpenExternal,
  showAcknowledgements = false,
  loadAcknowledgements = defaultLoad,
}: AboutDialogProps): ReactElement | null {
  const [data, setData] = useState<AckData | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAck, setShowAck] = useState(showAcknowledgements);
  // Latches once the (single) load has been kicked off, so re-renders never
  // re-trigger it — and so this effect needn't depend on `loading`/`data`, whose
  // updates would otherwise tear down the in-flight promise mid-load.
  const loadStarted = useRef(false);

  // Re-sync the expanded state to the requested mode each time the dialog opens.
  useEffect(() => {
    if (open) setShowAck(showAcknowledgements);
  }, [open, showAcknowledgements]);

  // Lazy-load the acknowledgements payload the first time the list is revealed.
  useEffect(() => {
    if (!open || !showAck || loadStarted.current) return;
    loadStarted.current = true;
    setLoading(true);
    setFailed(false);
    let cancelled = false;
    void loadAcknowledgements()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, showAck, loadAcknowledgements]);

  if (!open) return null;

  const openLink = (url: string): void => onOpenExternal?.(url);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="About Slate"
      className="mp-modal--about"
      footer={
        <button type="button" className="mp-btn mp-btn--primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="mp-about">
        <div className="mp-about__head">
          <SlateLogo className="mp-about__logo" />
          <div className="mp-about__id">
            <div className="mp-about__name">Slate</div>
            {version && <div className="mp-about__version">Version {version}</div>}
            <p className="mp-about__tagline">{TAGLINE}</p>
          </div>
        </div>

        <p className="mp-about__meta">
          {'By '}
          <button
            type="button"
            className="mp-about__link"
            onClick={() => openLink(WELOIN_URL)}
          >
            WELOIN
          </button>
          {' · '}
          <button
            type="button"
            className="mp-about__link"
            onClick={() => openLink(PRODUCT_URL)}
          >
            {PRODUCT_LABEL}
          </button>
        </p>
        <p className="mp-about__copyright">Copyright © 2026 WELOIN. All rights reserved.</p>

        <div className="mp-about__ack">
          {!showAck ? (
            <button type="button" className="mp-btn" onClick={() => setShowAck(true)}>
              Acknowledgements
            </button>
          ) : (
            <AckList data={data} loading={loading} failed={failed} />
          )}
        </div>
      </div>
    </Modal>
  );
}

function AckList({
  data,
  loading,
  failed,
}: {
  data: AckData | null;
  loading: boolean;
  failed: boolean;
}): ReactElement {
  if (failed) {
    return <p className="mp-about__ack-note">Could not load license information.</p>;
  }
  if (!data || loading) {
    return <p className="mp-about__ack-note">Loading licenses…</p>;
  }
  return (
    <>
      <p className="mp-about__ack-intro">
        Slate includes <strong>{data.items.length}</strong> third-party open-source
        components. Each is governed by its own license, reproduced below.
      </p>
      <ul className="mp-about__ack-list">
        {data.items.map((item) => (
          <AckRow
            key={`${item.ecosystem}:${item.name}@${item.version}`}
            item={item}
            texts={data.texts}
          />
        ))}
      </ul>
    </>
  );
}

function AckRow({
  item,
  texts,
}: {
  item: AckItem;
  texts: Record<string, string>;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const hasText = item.textRefs.length > 0;
  return (
    <li className="mp-about__ack-item">
      <button
        type="button"
        className="mp-about__ack-row"
        aria-expanded={hasText ? expanded : undefined}
        disabled={!hasText}
        onClick={() => hasText && setExpanded((v) => !v)}
      >
        <span className="mp-about__ack-pkg">{item.name}</span>
        <span className="mp-about__ack-ver">{item.version}</span>
        <span className="mp-about__ack-lic">{item.license || '—'}</span>
      </button>
      {expanded && hasText && (
        <pre className="mp-about__ack-text">
          {item.textRefs.map((h) => texts[h] ?? '').join('\n\n')}
        </pre>
      )}
    </li>
  );
}
